import React from "react";

/**
 * PlatformBadge — a rounded container in the platform's own brand colour that
 * holds its logo (or a text placeholder when the asset is still pending).
 *
 * The container treatment is deliberate and identical across the four platforms
 * currently shown in the LIVE ACROSS strip (a subset of the 13 QC platforms
 * PickQuick is live on): same radius, same padding, same dimensions per size
 * variant, brand-colour fill. This turns the constraint (three of the four
 * logos have their own baked-in background) into a system rather than a visual
 * inconsistency.
 *
 * Usage:
 *   <PlatformBadge id="blinkit" size="hero" />
 *   <PlatformBadge id="jiomart" size="chip" />
 *
 * Do NOT use this inside the hub diagram — that image is raster, not tokens.
 */

const PLATFORMS = {
  blinkit:   { bg: "#F8CB46", src: "/assets/platforms/blinkit.webp",   label: "Blinkit" },
  zepto:     { bg: "#5D2E8A", src: "/assets/platforms/zepto.webp",     label: "Zepto" },
  instamart: { bg: "#FC8019", src: "/assets/platforms/instamart.webp", label: "Swiggy Instamart" },
  jiomart:   { bg: "#0E5DA1", src: "/assets/platforms/jiomart.webp",   label: "JioMart" },
};

const SIZE_VARIANTS = {
  hero: { width: "132px", height: "56px", radius: "10px", padding: "8px 16px", placeholderFontSize: "22px" },
  chip: { width: "104px", height: "40px", radius: "8px",  padding: "6px 12px", placeholderFontSize: "16px" },
  form: { width: "112px", height: "44px", radius: "8px",  padding: "6px 14px", placeholderFontSize: "17px" },
};

export const PLATFORM_IDS = Object.keys(PLATFORMS);

export const PlatformBadge = ({ id, size = "hero" }) => {
  const platform = PLATFORMS[id];
  const variant = SIZE_VARIANTS[size] || SIZE_VARIANTS.hero;
  if (!platform) return null;

  return (
    <div
      data-testid={`platform-badge-${id}`}
      style={{
        width: variant.width,
        height: variant.height,
        borderRadius: variant.radius,
        backgroundColor: platform.bg,
        padding: variant.padding,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {platform.src ? (
        <img
          src={platform.src}
          alt={platform.label}
          style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <span
          style={{
            color: "#FFFFFF",
            fontFamily: "Arial, Helvetica, sans-serif",
            fontWeight: 800,
            fontSize: variant.placeholderFontSize,
            letterSpacing: "-0.01em",
          }}
        >
          {platform.label}
        </span>
      )}
    </div>
  );
};

export const PlatformBadgeRow = ({ size = "hero", gap = "20px" }) => (
  <div className="flex items-center flex-wrap justify-center" style={{ gap }}>
    {PLATFORM_IDS.map((id) => <PlatformBadge key={id} id={id} size={size} />)}
  </div>
);

export default PlatformBadge;
